import React, { useEffect, useRef, useState } from "react";
import {
  Bot,
  X,
  Minimize2,
  Maximize2,
  GripHorizontal,
  MessageSquare,
} from "lucide-react";
import ChatNorma from "./ChatNorma";

export default function NormaWidget() {
  const [open, setOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);

  // Manteniamo la chat montata anche da chiusa/minimizzata,
  // così non perde contenuto o stato.
  const [mounted, setMounted] = useState(false);

  const panelRef = useRef(null);
  const dragRef = useRef({
    dragging: false,
    startX: 0,
    startY: 0,
    startRight: 16,
    startBottom: 16,
  });

  const [position, setPosition] = useState({
    right: 16,
    bottom: 16,
  });

  useEffect(() => {
    if (open && !mounted) setMounted(true);
  }, [open, mounted]);

  useEffect(() => {
    const onMove = (e) => {
      if (!dragRef.current.dragging) return;

      const dx = e.clientX - dragRef.current.startX;
      const dy = e.clientY - dragRef.current.startY;

      let nextRight = dragRef.current.startRight - dx;
      let nextBottom = dragRef.current.startBottom - dy;

      nextRight = Math.max(8, Math.min(window.innerWidth - 320, nextRight));
      nextBottom = Math.max(8, Math.min(window.innerHeight - 120, nextBottom));

      setPosition({
        right: nextRight,
        bottom: nextBottom,
      });
    };

    const onUp = () => {
      dragRef.current.dragging = false;
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);

    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, []);

  const startDrag = (e) => {
    if (e.target.closest("button")) return;

    dragRef.current = {
      dragging: true,
      startX: e.clientX,
      startY: e.clientY,
      startRight: position.right,
      startBottom: position.bottom,
    };

    document.body.style.userSelect = "none";
    document.body.style.cursor = "grabbing";
  };

  const handleOpen = () => {
    setOpen(true);
    setMinimized(false);
  };

  const handleMinimize = () => {
    setOpen(true);
    setMinimized((v) => !v);
  };

  const handleClose = () => {
    setOpen(false);
    setMinimized(false);
  };

  return (
    <>
      {/* Bottone flottante */}
      {!open && (
        <button
          type="button"
          onClick={handleOpen}
          className="fixed bottom-4 right-4 z-40 flex items-center gap-2 rounded-full bg-emerald-600 px-4 py-3 text-white shadow-lg transition hover:bg-emerald-700"
          title="Parla con Norma"
        >
          <Bot size={18} />
          <span className="hidden sm:inline font-medium">Chiedi a Norma</span>
        </button>
      )}

      {/* Finestra chat sempre montata dopo la prima apertura */}
      {mounted && (
        <div
          ref={panelRef}
          style={{
            right: `${position.right}px`,
            bottom: `${position.bottom}px`,
          }}
          className={`fixed z-50 w-[92vw] max-w-md transition-all duration-200 ${open ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0"
            }`}
        >
          <div
            className={`overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl transition-all duration-200 ${minimized ? "h-[64px]" : "h-[70vh] max-h-[720px] min-h-[420px] resize-y"
              }`}
          >
            {/* Header / barra navigazione */}
            <div
              onMouseDown={startDrag}
              className="flex cursor-grab items-center justify-between border-b border-slate-200 bg-slate-900 px-3 py-3 text-white active:cursor-grabbing"
              title="Trascina la finestra"
            >
              <div className="flex min-w-0 items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-emerald-500/20 text-emerald-300">
                  <Bot size={18} />
                </div>

                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="truncate text-sm font-semibold">Norma AI</span>
                    <span className="rounded-full bg-emerald-500/20 px-2 py-0.5 text-[10px] font-medium text-emerald-200">
                      online
                    </span>
                  </div>
                  <div className="flex items-center gap-1 text-[11px] text-slate-300">
                    <MessageSquare size={12} />
                    <span>Assistente HACCP della tua azienda</span>
                  </div>
                </div>
              </div>

              <div className="ml-3 flex items-center gap-1">
                <span className="hidden sm:inline-flex items-center text-slate-400 mr-1">
                  <GripHorizontal size={15} />
                </span>

                <button
                  type="button"
                  onClick={handleMinimize}
                  className="rounded-lg p-2 transition hover:bg-slate-800"
                  title={minimized ? "Espandi" : "Riduci"}
                >
                  {minimized ? <Maximize2 size={16} /> : <Minimize2 size={16} />}
                </button>

                <button
                  type="button"
                  onClick={handleClose}
                  className="rounded-lg p-2 transition hover:bg-slate-800"
                  title="Chiudi"
                >
                  <X size={16} />
                </button>
              </div>
            </div>

            {/* Corpo: non smontiamo la chat, la nascondiamo soltanto */}
            <div className={`${minimized ? "hidden" : "flex h-[calc(100%-65px)] flex-col p-3"}`}>
              <ChatNorma embedded hidden={minimized || !open} />
            </div>
          </div>
        </div>
      )}
    </>
  );
}